import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { pastelThemes, themeConfig } from '../utils/pastelThemes';
import '../styles/NavBar.css';

const NavBar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentTheme, changeTheme, themeInfo } = useTheme();
    const { t, language, changeLanguage } = useLanguage();
    const [showSettings, setShowSettings] = useState(false);
    const [userInfo, setUserInfo] = useState(JSON.parse(localStorage.getItem('userInfo') || '{}'));
    const [editingNickname, setEditingNickname] = useState(false);
    const [newNickname, setNewNickname] = useState(userInfo.nickname || '');
    const [selectedInterests, setSelectedInterests] = useState(userInfo.interests || []);

    const interests = [
        { id: 'soccer', label: t('soccer'), emoji: '⚽' },
        { id: 'game', label: t('game'), emoji: '🎮' },
        { id: 'pokemon', label: t('pokemon'), emoji: '🎭' },
        { id: 'music', label: t('music'), emoji: '🎵' },
        { id: 'art', label: t('art'), emoji: '🎨' },
        { id: 'science', label: t('science'), emoji: '🔬' }
    ];

    const handleSaveSettings = () => {
        const updatedInfo = {
            ...userInfo,
            nickname: newNickname,
            interests: selectedInterests
        };
        localStorage.setItem('userInfo', JSON.stringify(updatedInfo));
        setUserInfo(updatedInfo);
        setEditingNickname(false);
        window.location.reload(); // Refresh to update the course list
    };

    const toggleInterest = (interestId) => {
        if (selectedInterests.includes(interestId)) {
            setSelectedInterests(selectedInterests.filter(id => id !== interestId));
        } else {
            setSelectedInterests([...selectedInterests, interestId]);
        }
    };

    return (
        <>
            <nav className="navbar">
                <div className="navbar-container">
                    <div className="navbar-left">
                        <button 
                            className="home-button"
                            onClick={() => navigate('/courses')}
                        >
                            <span className="home-icon">🏠</span>
                            <span className="home-text">DevKids</span>
                        </button>
                    </div>
                    
                    <div className="navbar-right">
                        <button 
                            className="settings-button"
                            onClick={() => setShowSettings(true)}
                        >
                            <span className="settings-icon">⚙️</span>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Settings Modal */}
            {showSettings && (
                <div className="modal-overlay" onClick={() => setShowSettings(false)}>
                    <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{t('settingsTitle')}</h2>
                            <button 
                                className="close-button"
                                onClick={() => setShowSettings(false)}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal-content">
                            {/* Nickname Section */}
                            <div className="settings-section">
                                <h3>{t('nickname')}</h3>
                                {editingNickname ? (
                                    <div className="nickname-edit">
                                        <input
                                            type="text"
                                            value={newNickname}
                                            onChange={(e) => setNewNickname(e.target.value)}
                                            placeholder={t('enterNickname')}
                                            className="nickname-input"
                                        />
                                        <button 
                                            className="save-nickname"
                                            onClick={() => setEditingNickname(false)}
                                        >
                                            {t('save')}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="nickname-display">
                                        <span>{newNickname}</span>
                                        <button 
                                            className="edit-nickname"
                                            onClick={() => setEditingNickname(true)}
                                        >
                                            {t('edit')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Language Section */}
                            <div className="settings-section">
                                <h3>{t('language')}</h3>
                                <div className="language-grid">
                                    <button
                                        className={`language-option ${language === 'ko' ? 'active' : ''}`}
                                        onClick={() => changeLanguage('ko')}
                                    >
                                        🇰🇷 한국어
                                    </button>
                                    <button
                                        className={`language-option ${language === 'en' ? 'active' : ''}`}
                                        onClick={() => changeLanguage('en')}
                                    >
                                        🇺🇸 English
                                    </button>
                                </div>
                            </div>

                            {/* Theme Section */}
                            <div className="settings-section">
                                <h3>{t('theme')}</h3>
                                <div className="theme-grid">
                                    {Object.entries(pastelThemes).map(([key, themeData]) => (
                                        <button
                                            key={key}
                                            className={`theme-option ${currentTheme === key ? 'active' : ''}`}
                                            onClick={() => changeTheme(key)}
                                            style={{
                                                backgroundColor: themeData.primary,
                                                borderColor: themeData['primary-dark']
                                            }}
                                        >
                                            <span className="theme-emoji">{themeConfig[key].emoji}</span>
                                            <span className="theme-name">{themeConfig[key].name}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Interests Section */}
                            <div className="settings-section">
                                <h3>{t('interests')}</h3>
                                <div className="interests-grid">
                                    {interests.map(interest => (
                                        <button
                                            key={interest.id}
                                            className={`interest-option ${selectedInterests.includes(interest.id) ? 'selected' : ''}`}
                                            onClick={() => toggleInterest(interest.id)}
                                        >
                                            <span className="interest-emoji">{interest.emoji}</span>
                                            <span className="interest-label">{interest.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button 
                                className="save-button"
                                onClick={handleSaveSettings}
                            >
                                {t('saveChanges')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default NavBar;